/**
 * NOVAVICT — free "Diskretes Reiseetui" with every Kudzu bottle.
 *
 * Keeps the gift line in sync with the cart by rewriting the theme's own
 * /cart/add.js and /cart/change.js requests before they are sent. Because the
 * gift is added or removed inside the request the theme already made, Shopify's
 * section rendering returns markup that includes it — no second render pass and
 * no dependency on Horizon's internals.
 *
 * Price is handled by the automatic discount "Gratis Reiseetui zu jeder
 * Kudzu-Flasche". This file only controls whether the line is present.
 */
(function () {
  'use strict';

  /**
   * IDs come from window.NOVAVICT_GIFT, which layout/theme.liquid renders from
   * the live catalogue on every request. The literals below are only a safety
   * net — hardcoding variant IDs is exactly what broke this once already: the
   * bottle gained "2 Flaschen" and "4 Flaschen" pack variants, and because only
   * the single-bottle ID was listed, the gift quietly stopped attaching to every
   * subscription tier while one-time purchases kept working.
   */
  var CONFIG = window.NOVAVICT_GIFT || {};

  var GIFT = Number(CONFIG.giftVariant) || 53918448124232;
  var GIFT_PRODUCT = Number(CONFIG.giftProduct) || 10234329563464;
  var TRIGGER_PRODUCT = Number(CONFIG.triggerProduct) || 10234329661768;
  var TRIGGERS = (CONFIG.triggerVariants || []).length
    ? CONFIG.triggerVariants.map(Number)
    : [53918448255304, 54138026525000, 54138026557768];

  // Form plumbing that must not be copied into a /cart/add.js JSON item.
  var FORM_ONLY = { sections: 1, sections_url: 1, form_type: 1, utf8: 1, return_to: 1 };

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var nativeFetch = window.fetch.bind(window);
  var busy = false;

  function isTrigger(id) {
    return TRIGGERS.indexOf(Number(id)) !== -1;
  }

  /**
   * Cart lines carry a product id, so matching on it covers pack variants added
   * after this page was served. The variant check stays as a second chance.
   */
  function isTriggerLine(line) {
    return line.product_id === TRIGGER_PRODUCT || isTrigger(line.variant_id);
  }

  function isGiftLine(line) {
    return line.product_id === GIFT_PRODUCT || line.variant_id === GIFT;
  }

  function urlOf(input) {
    if (typeof input === 'string') return input;
    if (input instanceof URL) return input.href;
    if (input && typeof input.url === 'string') return input.url;
    return '';
  }

  function hits(url, endpoint) {
    return url.split('?')[0].indexOf('/cart/' + endpoint) !== -1;
  }

  function getCart() {
    return nativeFetch(root + 'cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then(function (r) {
      return r.json();
    });
  }

  /** Reads a request body into a plain object, whatever shape it arrived in. */
  function readBody(body) {
    if (!body) return null;

    if (body instanceof FormData || body instanceof URLSearchParams) {
      var out = {};
      body.forEach(function (value, key) {
        out[key] = value;
      });
      return out;
    }

    if (typeof body === 'string') {
      try {
        return JSON.parse(body);
      } catch (e) {
        var parsed = {};
        new URLSearchParams(body).forEach(function (value, key) {
          parsed[key] = value;
        });
        return parsed;
      }
    }

    return null;
  }

  /** Pulls `properties[Foo]` keys out of a flattened form body. */
  function extractProperties(flat) {
    var props = null;
    Object.keys(flat).forEach(function (key) {
      var match = key.match(/^properties\[(.+)\]$/);
      if (!match) return;
      props = props || {};
      props[match[1]] = flat[key];
    });
    return props;
  }

  function jsonRequest(url, payload) {
    return [
      url,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'same-origin',
      },
    ];
  }

  /**
   * Turns a single add-to-cart into a two-line add when a Kudzu bottle is going
   * into a cart that has no gift yet.
   */
  function rewriteAdd(url, flat) {
    var items = Array.isArray(flat.items) ? flat.items.slice() : null;

    if (!items) {
      if (!flat.id) return null;

      // Carry every field the form sent — `selling_plan` above all, since
      // dropping it would turn a subscription into a one-time purchase. Only
      // Shopify's own form plumbing and the properties[] keys are filtered out.
      var single = {};
      Object.keys(flat).forEach(function (key) {
        if (FORM_ONLY[key] || /^properties\[/.test(key)) return;
        single[key] = flat[key];
      });

      single.id = Number(flat.id);
      single.quantity = Number(flat.quantity || 1);
      if (single.selling_plan) single.selling_plan = Number(single.selling_plan);

      var props = extractProperties(flat);
      if (props) single.properties = props;
      items = [single];
    }

    var addsTrigger = items.some(function (item) {
      return isTrigger(item.id);
    });
    if (!addsTrigger) return null;

    return getCart().then(function (cart) {
      var hasGift = cart.items.some(function (line) {
        return isGiftLine(line);
      });
      if (hasGift) return null;

      items.push({ id: GIFT, quantity: 1 });

      var payload = { items: items };
      if (flat.sections) payload.sections = flat.sections;
      if (flat.sections_url) payload.sections_url = flat.sections_url;

      return jsonRequest(url, payload);
    });
  }

  /**
   * Turns "remove the last Kudzu bottle" into an update that clears the gift in
   * the same request, so the gift never lingers in a bottle-less cart.
   */
  function rewriteChange(url, flat) {
    if (Number(flat.quantity) !== 0) return null;

    return getCart().then(function (cart) {
      var giftLine = null;
      var targetLine = null;
      var remainingTriggers = 0;

      cart.items.forEach(function (line, index) {
        if (isGiftLine(line)) giftLine = line;

        var isTarget = flat.line
          ? Number(flat.line) === index + 1
          : String(flat.id) === String(line.key) || Number(flat.id) === line.variant_id;

        if (isTarget) targetLine = line;
        else if (isTriggerLine(line)) remainingTriggers += 1;
      });

      if (!giftLine || !targetLine) return null;
      if (!isTriggerLine(targetLine)) return null;
      if (remainingTriggers > 0) return null;

      var updates = {};
      updates[targetLine.key] = 0;
      updates[giftLine.key] = 0;

      var payload = { updates: updates };
      if (flat.sections) payload.sections = flat.sections;
      if (flat.sections_url) payload.sections_url = flat.sections_url;

      return jsonRequest(root + 'cart/update.js', payload);
    });
  }

  window.fetch = function (input, init) {
    var url = urlOf(input);

    if (busy || !url || url.indexOf('/cart/') === -1) {
      return nativeFetch(input, init);
    }

    var isAdd = hits(url, 'add');
    var isChange = hits(url, 'change');
    if (!isAdd && !isChange) return nativeFetch(input, init);

    var flat = readBody(init && init.body);
    if (!flat) return nativeFetch(input, init);

    busy = true;

    var plan = isAdd ? rewriteAdd(url, flat) : rewriteChange(url, flat);

    return Promise.resolve(plan)
      .catch(function () {
        return null;
      })
      .then(function (rewritten) {
        busy = false;
        if (!rewritten) return nativeFetch(input, init);
        return nativeFetch(rewritten[0], rewritten[1]);
      });
  };

  /**
   * Safety net for cart changes that never went through fetch — a full-page
   * form post, a restored bfcache page, a cart edited in another tab. Corrects
   * the cart quietly, and only reloads when the visible page is now wrong.
   */
  function reconcile() {
    getCart()
      .then(function (cart) {
        var gift = null;
        var triggers = 0;

        cart.items.forEach(function (line) {
          if (isGiftLine(line)) gift = line;
          else if (isTriggerLine(line)) triggers += 1;
        });

        if (triggers > 0 && !gift) return { id: GIFT, quantity: 1 };
        if (!triggers && gift) return { id: GIFT, quantity: 0 };
        if (gift && gift.quantity > 1) return { id: GIFT, quantity: 1 };
        return null;
      })
      .then(function (fix) {
        if (!fix) return null;

        var updates = {};
        updates[fix.id] = fix.quantity;

        return nativeFetch(root + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: updates }),
          credentials: 'same-origin',
        }).then(function () {
          var onCartPage = /\/cart(\/|$|\?)/.test(window.location.pathname);
          if (!onCartPage) return;
          if (sessionStorage.getItem('novavict-gift-reloaded')) return;
          sessionStorage.setItem('novavict-gift-reloaded', '1');
          window.location.reload();
        });
      })
      .catch(function () {
        /* never block the page over a gift line */
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcile);
  } else {
    reconcile();
  }
})();
